import { useUser } from '@/app/providers';
import { ButtonIconText } from '@/components';
import { PageContentCard } from '@/components/layout/PageContentCard';
import { hasFeature } from '@/domain/authentication';
import { useSidePanelHandler } from '@/hooks/useSidePanelHandler';
import { useTranslation } from 'react-i18next';
import { useDeviceRepairs } from '../hooks';
import type { RepairHistoryEntry } from '../types';
import { RepairHistoryCardRow } from './RepairHistoryCardRow';
import { RepairHistorySidePanel } from './RepairHistorySidePanel';

interface RepairHistoryCardProps {
  deviceId: string;
}

// The side panel is either adding a new entry or editing an existing one.
type RepairPanelContent = { type: 'add' } | { type: 'edit'; entry: RepairHistoryEntry };

// RepairHistoryCard lists a device's repair entries as cards. Only users with the
// repairHistory.write feature can add, edit or delete; everyone else reads and opens documents.
// For read-only users the section is hidden entirely when there are no entries.
export function RepairHistoryCard({ deviceId }: RepairHistoryCardProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const canWrite = Boolean(user && hasFeature(user, 'repairHistory.write'));

  const { data: repairs, isLoading } = useDeviceRepairs(deviceId);
  const sidePanel = useSidePanelHandler<RepairPanelContent>();

  const entries = repairs ?? [];
  const panelContent = sidePanel.content;
  // Resolve the edited entry from the live query cache so the panel reflects the latest data.
  const editedEntry =
    panelContent?.type === 'edit'
      ? (entries.find((entry) => entry.id === panelContent.entry.id) ?? null)
      : null;

  if (!canWrite && entries.length === 0) {
    return null;
  }

  return (
    <PageContentCard className="w-full">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-onsurface text-lg font-semibold">
          {t('repairHistory.title')}
        </h2>
        {canWrite && (
          <ButtonIconText
            type="button"
            iconName="plus"
            onClick={() => sidePanel.open({ type: 'add' })}
          >
            {t('repairHistory.actions.add')}
          </ButtonIconText>
        )}
      </div>

      <RepairHistoryList
        isLoading={isLoading}
        entries={entries}
        canWrite={canWrite}
        onEdit={(entry) => sidePanel.open({ type: 'edit', entry })}
      />

      {canWrite && (
        <RepairHistorySidePanel
          deviceId={deviceId}
          entry={editedEntry}
          isOpen={sidePanel.isOpen}
          panelKey={sidePanel.panelKey}
          onClose={sidePanel.close}
        />
      )}
    </PageContentCard>
  );
}

function RepairHistoryList({
  isLoading,
  entries,
  canWrite,
  onEdit,
}: {
  isLoading: boolean;
  entries: RepairHistoryEntry[];
  canWrite: boolean;
  onEdit: (entry: RepairHistoryEntry) => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return <p className="text-muted text-sm">{t('common.loading')}</p>;
  }

  if (entries.length === 0) {
    return <p className="text-muted text-sm">{t('repairHistory.empty')}</p>;
  }

  return (
    <div className="space-y-2">
      {entries.map((entry) => (
        <RepairHistoryCardRow
          key={entry.id}
          entry={entry}
          onEdit={canWrite ? () => onEdit(entry) : undefined}
        />
      ))}
    </div>
  );
}
